import { cn } from "./cn";

/**
 * FORM ALANININ ALTI HALI — TEK YERDE
 *
 * ================================================================
 * NEDEN AYRI BIR DOSYA
 * ================================================================
 * `Alan`, `MetinAlani`, `Metin` ve `Secim` ayni sozlesmeyi tasiyor ve
 * `MetinAlani` kendi yorumunda bunu zaten sart kosuyor: "iki bilesen
 * ayni sozlesmeyi tasimazsa, birinde duzeltilen erisilebilirlik
 * hatasi otekinde yasamaya devam eder."
 *
 * O sart DORT dosyaya elle kopyalanmis bir sinif dizisiyle
 * tutulmuyordu. Nitekim tutulamamisti: hata metni dordunde de
 * `hata-500` idi ve o ton kendi acik zemininde WCAG AA'nin altinda
 * kaliyor (teslim notu 4,19 olcuyor; dogrusu `hata-700`, 6,85).
 * Tek bir dosyada olsaydi bir kez duzeltilirdi.
 *
 * Cizim paketi form alanina ALTI hal veriyor (`DurumMatrisi.dc.html`):
 * bos · odakta · dolu · hata · devre disi · salt okunur.
 * "Bos" ile "dolu" arasinda gorsel fark YOK ve bu bilincli — alanin
 * doldugunu soyleyen sey icerigin kendisi; kutuyu ayrica boyamak,
 * doldurulmus bir formu alarm tablosuna cevirirdi.
 */

export interface AlanBicimiSecenekleri {
  /** Hata hali — kirmizi halka + acik kirmizi zemin */
  hata?: boolean;
  /** Salt okunur — kutu degil, KILITLI DEGER gibi gorunur */
  saltOkunur?: boolean;
  /**
   * 44px dokunma hedefi.
   *
   * `<input>` ve `<select>` icin ZORUNLU. `<textarea>` icin degil:
   * yuksekligini `rows` veriyor ve zaten tek satirdan buyuk.
   */
  dokunmaHedefi?: boolean;
  ekSinif?: string;
}

export function alanBicimi({
  hata = false,
  saltOkunur = false,
  dokunmaHedefi = false,
  ekSinif,
}: AlanBicimiSecenekleri = {}): string {
  return cn(
    dokunmaHedefi && "min-h-dokunma",
    // `text-base` — 16px. Kucultmeyin: iOS Safari 16px'in ALTINDAKI bir
    // alana odaklanildiginda sayfayi yakinlastirir ve geri almaz.
    "rounded-alan px-3 py-2 text-base",
    "bg-notr-0 text-notr-900",
    "ring-1 ring-inset ring-notr-300",
    /*
      HALKA BUYUYEREK GELIYOR — cizim paketinin "odakta" hali.
      Gecis GUNDELIK kovada (`sure-hizli`): odaklanma gunde yuzlerce
      kez oluyor, uzatilirsa her sekmede beklenir.
    */
    "transition-[box-shadow,background-color] duration-(--sure-hizli) ease-cikis",
    "focus:ring-2 focus:ring-marka-600",
    /*
      `outline-none` KALDIRILDI.

      Eskiden buradaydi ve `tokens.css`'teki tek merkezi
      `:focus-visible` kuralini alanlarda SUSTURUYORDU: ic halka
      goruluyordu ama kutunun DISINDAKI 2px'lik odak halkasi hic
      cizilmiyordu. Cizim paketi ikisini birden gosteriyor —
      ic halka alani, dis halka odagi isaretliyor ve dis halka
      koyu/acik her zeminde ayni yerde duruyor.
    */
    "disabled:cursor-not-allowed disabled:bg-notr-50 disabled:text-notr-500 disabled:ring-notr-200",
    /*
      SALT OKUNUR KUTU GIBI DURMUYOR. Kenarligi olan bir alan
      "buraya yazabilirsin" sozu verir; salt okunur alan o sozu
      tutamaz. Zemin dolu, halka yok — bir DEGER gosterimi.
      Devre disi ile ayni da degil: devre disi "sonra acilabilir",
      salt okunur "bu deger boyle".
    */
    saltOkunur && "bg-notr-100 text-notr-800 ring-0",
    hata && "bg-hata-50 ring-hata-500 focus:ring-hata-500",
    ekSinif,
  );
}

/** Etiket — gorsel olarak gizlenebilir ama ekran okuyucuda HER ZAMAN var. */
export function etiketBicimi(gizli?: boolean): string {
  return gizli ? "sr-only" : "text-sm font-medium text-notr-800";
}

/** Yardim metni. `notr-500` beyaz zeminde 4,83 — AA gecer. */
export const YARDIM_BICIMI = "text-xs text-notr-600";

/**
 * Hata metni.
 *
 * `hata-700`, `hata-500` DEGIL: teslim paketi olctu, `hata-500` kendi
 * acik kirmizi zemininde 4,19 veriyor ve AA'nin altinda kaliyor.
 * Renk tek sinyal de degil — yaninda ikon var, metin zaten yaziyor ve
 * `role="alert"` ile duyuruluyor.
 */
export const HATA_BICIMI = "flex items-start gap-1.5 text-xs font-medium text-hata-700";
